import Card from "components/DesignSystem/Card/Card";
import { useMemo } from "react";
import { ActionableItems } from "./ActionableItems";
import { AssignAgentFilter } from "./AssignAgentFilter";
import { useAuth } from "hooks/useAuth";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { useGetChatTicketsQuery } from "store/apis/chatTicket";
import { useRoleBasedView } from "hooks/useRoleBasedView";

export const AgentDashboard = () => {
  const { uuid } = useAuth();
  const queryData = useQuery();
  const { update } = useUpdateQuery();
  const { isAdmin } = useRoleBasedView();

  const selectedAssignee = queryData.get("assignee_profile_ids") || null;
  const agentIdsParam = queryData.get("agent_ids") || null;

  const { data: assignAgents = [], isSuccess } = useGetAssignAgentsQuery(
    {},
    { skip: !isAdmin }
  );

  const effectiveAgentIds = useMemo(() => {
    if (agentIdsParam) return agentIdsParam;
    if (!isSuccess || !uuid || assignAgents.length === 0) return null;
    const userIsInAssignAgents = assignAgents.some(
      (agent) => agent.profile_id === uuid
    );
    return userIsInAssignAgents ? uuid : null;
  }, [agentIdsParam, isSuccess, uuid, assignAgents]);

  const selectedAgents = effectiveAgentIds ? effectiveAgentIds.split(",") : [];

  const setSelectedAgents = (agents: string[]) => {
    update({
      query: "agent_ids",
      value: agents.length > 0 ? agents.join(",") : null,
    });
  };

  const { data: actionableData } = useGetChatTicketsQuery({
    assignee_profile_ids: selectedAssignee,
    agent_ids: effectiveAgentIds,
    resolved_status: false,
    page_num: 1,
    is_internal_ticket: true,
  });
  const actionablesCount = actionableData?.unresolved_tickets ?? 0;

  return (
    <Card.Root className="t-w-full" borderRounded="lg">
      <Card.Header className="t-flex t-justify-between t-items-center t-w-full">
        <span className="t-text-subtext t-font-semibold">
          Actionables
          {actionablesCount > 0 && (
            <span className="t-ml-2 t-text-caption t-text-text-30">
              ({actionablesCount})
            </span>
          )}
        </span>
        <AssignAgentFilter
          selectedAgents={selectedAgents}
          setSelectedAgents={setSelectedAgents}
        />
      </Card.Header>
      <Card.Body>
        <ActionableItems defaultAssigneeProfileId={uuid || null} />
      </Card.Body>
    </Card.Root>
  );
};
